import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { createApiKeyBodySchema } from "@aws-portal/core/schemas/api-keys/api-keys.model";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui";
import { Code } from "@/components/ui/Code";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { config } from "@/utils/config";
export const CreateApiKeyModal = ({ isOpen, onClose, onSubmit, isLoading, products, defaultProductCode, productsLoading })=>{
    const [createdKey, setCreatedKey] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [showFullKey, setShowFullKey] = useState(false);
    const [copyCurlSuccess, setCopyCurlSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [selectedProductCode, setSelectedProductCode] = useState(defaultProductCode || "");
    useEffect(()=>{
        if (defaultProductCode && !selectedProductCode) {
            setSelectedProductCode(defaultProductCode);
        }
    }, [
        defaultProductCode
    ]);
    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        resolver: zodResolver(createApiKeyBodySchema)
    });
    const handleFormSubmit = async (data)=>{
        try {
            setSubmitError(null);
            const result = await onSubmit(data, selectedProductCode);
            setCreatedKey(result);
            reset();
        } catch (error) {
            const fallbackMessage = "Failed to create API key";
            setSubmitError(error instanceof Error ? error.message : fallbackMessage);
        }
    };
    const handleCopyToClipboard = async (value)=>{
        try {
            await navigator.clipboard.writeText(value);
            setCopySuccess(true);
            setTimeout(()=>setCopySuccess(false), 2000);
        } catch (error) {
        // Failed to copy to clipboard
        }
    };
    const handleCopyCurl = async (curl)=>{
        try {
            await navigator.clipboard.writeText(curl);
            setCopyCurlSuccess(true);
            setTimeout(()=>setCopyCurlSuccess(false), 2000);
        } catch (error) {
        // Failed to copy to clipboard
        }
    };
    const handleClose = ()=>{
        setCreatedKey(null);
        setCopySuccess(false);
        setCopyCurlSuccess(false);
        setShowFullKey(false);
        setSelectedProductCode(defaultProductCode || "");
        setSubmitError(null);
        reset();
        onClose();
    };
    const maskApiKey = (key)=>{
        return "•".repeat(key.length);
    };
    const generateCurlExample = (apiKey, productCode)=>{
        // KeyGen product uses different endpoint and payload
        if (productCode.toLowerCase().includes("keygen")) {
            return `curl --location 'https://qdea-1-aws-eastus.qrypt.com/api/v1/user/generate-stateful-sample' \\
--header 'Content-Type: application/json' \\
--header 'X-API-Key: ${apiKey}' \\
--data '{
    "seed": "<unique base64 seed>",
    "sample_size": 114,
    "ttl": 3600
}'`;
        }
        // Default EaaS example
        return `curl --location '${config.eaasApiUrl}' \\
--header 'Content-Type: application/json' \\
--header 'X-API-Key: ${apiKey}' \\
--data '{
    "block_size": 32,
    "block_count": 5
}'`;
    };
    if (!isOpen) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50",
        children: /*#__PURE__*/ _jsx("div", {
            className: "relative top-1/2 transform -translate-y-1/2 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white",
            children: /*#__PURE__*/ _jsx("div", {
                className: "mt-3",
                children: createdKey ? // Success state - show the created API key
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between mb-4",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium text-gray-900",
                                    children: "API Key Created Successfully"
                                }),
                                /*#__PURE__*/ _jsxs("button", {
                                    onClick: handleClose,
                                    className: "text-gray-400 hover:text-gray-600",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "sr-only",
                                            children: "Close"
                                        }),
                                        /*#__PURE__*/ _jsx("svg", {
                                            className: "h-6 w-6",
                                            fill: "none",
                                            viewBox: "0 0 24 24",
                                            stroke: "currentColor",
                                            children: /*#__PURE__*/ _jsx("path", {
                                                strokeLinecap: "round",
                                                strokeLinejoin: "round",
                                                strokeWidth: 2,
                                                d: "M6 18L18 6M6 6l12 12"
                                            })
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md",
                            children: /*#__PURE__*/ _jsxs("p", {
                                className: "text-sm text-yellow-800",
                                children: [
                                    /*#__PURE__*/ _jsx("strong", {
                                        children: "Important:"
                                    }),
                                    " This is the only time you'll see this API key. Please copy it now."
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md",
                            children: /*#__PURE__*/ _jsxs("p", {
                                className: "text-sm text-blue-800",
                                children: [
                                    /*#__PURE__*/ _jsx("strong", {
                                        children: "Note:"
                                    }),
                                    " Your API key will be effective in 30-90 seconds."
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-4",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-gray-700 mb-1",
                                            children: "API Key Name"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-sm text-gray-900",
                                            children: createdKey.name
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-gray-700 mb-2",
                                            children: "API Key"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-2",
                                            children: [
                                                showFullKey ? /*#__PURE__*/ _jsx(Code, {
                                                    className: "!mb-0",
                                                    children: createdKey.value
                                                }) : /*#__PURE__*/ _jsx(Code, {
                                                    className: "!mb-0",
                                                    children: maskApiKey(createdKey.value)
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center justify-between",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs(Button, {
                                                            variant: "simple",
                                                            color: "navy-light",
                                                            onClick: ()=>setShowFullKey(!showFullKey),
                                                            className: "!flex !items-center",
                                                            children: [
                                                                /*#__PURE__*/ _jsx("svg", {
                                                                    className: "w-4 h-4 mr-1 flex-shrink-0",
                                                                    fill: "none",
                                                                    stroke: "currentColor",
                                                                    viewBox: "0 0 24 24",
                                                                    children: showFullKey ? /*#__PURE__*/ _jsx("path", {
                                                                        strokeLinecap: "round",
                                                                        strokeLinejoin: "round",
                                                                        strokeWidth: 2,
                                                                        d: "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                                                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx("path", {
                                                                                strokeLinecap: "round",
                                                                                strokeLinejoin: "round",
                                                                                strokeWidth: 2,
                                                                                d: "M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                                            }),
                                                                            /*#__PURE__*/ _jsx("path", {
                                                                                strokeLinecap: "round",
                                                                                strokeLinejoin: "round",
                                                                                strokeWidth: 2,
                                                                                d: "M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                                            })
                                                                        ]
                                                                    })
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "whitespace-nowrap",
                                                                    children: showFullKey ? "Hide" : "Show"
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx(Button, {
                                                            variant: "simple",
                                                            color: "navy-90",
                                                            onClick: ()=>handleCopyToClipboard(createdKey.value),
                                                            children: copySuccess ? "Copied!" : "Copy Key"
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                selectedProductCode.toLowerCase().includes("keygen") ? /*#__PURE__*/ _jsx("div", {
                                    className: "p-4 bg-gray-50 border border-gray-200 rounded-md",
                                    children: /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-gray-700",
                                        children: "To learn how to use your KeyGen API key, please visit the KeyGen documentation."
                                    })
                                }) : /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsx("label", {
                                            className: "block text-sm font-medium text-gray-700 mb-2",
                                            children: "Usage Example"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-2",
                                            children: [
                                                showFullKey ? /*#__PURE__*/ _jsx(Code, {
                                                    className: "!mb-0",
                                                    children: generateCurlExample(createdKey.value, selectedProductCode)
                                                }) : /*#__PURE__*/ _jsx(Code, {
                                                    className: "!mb-0",
                                                    children: generateCurlExample(maskApiKey(createdKey.value), selectedProductCode)
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "flex items-center justify-end",
                                                    children: /*#__PURE__*/ _jsx(Button, {
                                                        variant: "simple",
                                                        color: "navy-90",
                                                        onClick: ()=>handleCopyCurl(generateCurlExample(createdKey.value, selectedProductCode)),
                                                        children: copyCurlSuccess ? "Copied!" : "Copy cURL"
                                                    })
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-6 flex justify-end",
                            children: /*#__PURE__*/ _jsx(Button, {
                                variant: "simple",
                                color: "navy-90",
                                onClick: handleClose,
                                children: "Close"
                            })
                        })
                    ]
                }) : // Form state - create new API key
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between mb-4",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium text-gray-900",
                                    children: "Create New API Key"
                                }),
                                /*#__PURE__*/ _jsxs("button", {
                                    onClick: handleClose,
                                    className: "text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed",
                                    disabled: isLoading,
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "sr-only",
                                            children: "Close"
                                        }),
                                        /*#__PURE__*/ _jsx("svg", {
                                            className: "h-6 w-6",
                                            fill: "none",
                                            viewBox: "0 0 24 24",
                                            stroke: "currentColor",
                                            children: /*#__PURE__*/ _jsx("path", {
                                                strokeLinecap: "round",
                                                strokeLinejoin: "round",
                                                strokeWidth: 2,
                                                d: "M6 18L18 6M6 6l12 12"
                                            })
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("form", {
                            onSubmit: handleSubmit(handleFormSubmit),
                            children: [
                                submitError && /*#__PURE__*/ _jsx("div", {
                                    className: "mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700",
                                    children: submitError
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mb-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs(Select, {
                                            label: "Product *",
                                            id: "product",
                                            value: selectedProductCode,
                                            onChange: (e)=>setSelectedProductCode(e.target.value),
                                            disabled: productsLoading || !products || products.length === 0,
                                            required: true,
                                            children: [
                                                !selectedProductCode && /*#__PURE__*/ _jsx("option", {
                                                    value: "",
                                                    children: productsLoading ? "Loading products..." : "Select a product"
                                                }),
                                                products?.map((product)=>/*#__PURE__*/ _jsx("option", {
                                                        value: product.productCode,
                                                        children: product.productName
                                                    }, product.productCode))
                                            ]
                                        }),
                                        (!products || products.length === 0) && !productsLoading && /*#__PURE__*/ _jsx("p", {
                                            className: "mt-1 text-sm text-yellow-600",
                                            children: "No products available. Active contracts required."
                                        }),
                                        products && products.length > 0 && /*#__PURE__*/ _jsx("p", {
                                            className: "mt-1 text-xs text-gray-500",
                                            children: "Only products with active contracts shown. API key limits apply."
                                        }),
                                        selectedProductCode && (()=>{
                                            const product = products?.find((p)=>p.productCode === selectedProductCode);
                                            if (!product?.usagePlan) return null;
                                            const available = product.usagePlan.availableApiKeySlots;
                                            // Show available slots or error if none available
                                            if (available > 0) {
                                                return /*#__PURE__*/ _jsxs("p", {
                                                    className: "mt-2 text-sm text-gray-600",
                                                    children: [
                                                        available,
                                                        " API key slot",
                                                        available !== 1 ? "s" : "",
                                                        " ",
                                                        "available"
                                                    ]
                                                });
                                            } else if (product.reasonIfUnavailable) {
                                                return /*#__PURE__*/ _jsx("div", {
                                                    className: "mt-2 p-2 bg-red-50 rounded text-sm text-red-600",
                                                    children: product.reasonIfUnavailable
                                                });
                                            }
                                            return null;
                                        })()
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "mb-4",
                                    children: /*#__PURE__*/ _jsx(Input, {
                                        label: "Name *",
                                        id: "name",
                                        ...register("name"),
                                        placeholder: "Enter API key name",
                                        errors: errors.name
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex justify-end space-x-3 pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx(Button, {
                                            type: "button",
                                            variant: "simple",
                                            buttonStyle: "flat",
                                            onClick: handleClose,
                                            disabled: isLoading,
                                            children: "Cancel"
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            type: "submit",
                                            variant: "simple",
                                            color: "navy-90",
                                            disabled: isLoading || !selectedProductCode,
                                            children: isLoading ? "Creating..." : "Create API Key"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        })
    });
};
